/**
 * Modal for exporting referral data to CSV with column selection and date filtering.
 */
import React, { useState } from "react";
import {
  Modal,
  Button,
  Checkbox,
  Stack,
  Group,
  Text,
  Divider,
  Collapse,
  Box,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import {
  IconDownload,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import { exportReferralCSV } from "@/utils/api/referrals";

interface ReferralExportModalProps {
  opened: boolean;
  onClose: () => void;
}

// Column definitions organized by category
const COLUMN_CATEGORIES = {
  referee: {
    label: "Referee Information",
    columns: [
      { value: "referee_email", label: "Referee Email" },
      { value: "referee_name", label: "Referee Name" },
    ],
  },
  referrer: {
    label: "Referrer Information",
    columns: [{ value: "referrer_email", label: "Referrer Email" }],
  },
  details: {
    label: "Referral Details",
    columns: [
      { value: "signup_source", label: "Signup Type" },
      { value: "payment_status", label: "Payment Status" },
      { value: "reward_amount", label: "Reward Amount ($)" },
    ],
  },
  financial: {
    label: "Financial",
    columns: [
      { value: "total_paid", label: "Total Paid ($)" },
      { value: "total_pending", label: "Total Pending ($)" },
      { value: "referrer_rewards_count", label: "Referrer Rewards Count" },
      { value: "referee_rewards_count", label: "Referee Rewards Count" },
    ],
  },
  metadata: {
    label: "Metadata",
    columns: [
      { value: "created_at", label: "Created Date" },
      { value: "updated_at", label: "Updated Date" },
      { value: "notes", label: "Notes" },
    ],
  },
  internal: {
    label: "Internal IDs",
    columns: [
      { value: "referral_id", label: "Referral ID" },
      { value: "referrer_id", label: "Referrer User ID" },
      { value: "referee_id", label: "Referee User ID" },
    ],
  },
};

// All user-facing columns (exclude internal IDs by default)
const DEFAULT_COLUMNS = [
  "referrer_email",
  "referee_email",
  "referee_name",
  "signup_source",
  "payment_status",
  "reward_amount",
  "total_paid",
  "total_pending",
  "referrer_rewards_count",
  "referee_rewards_count",
  "created_at",
  "updated_at",
  "notes",
];

const ReferralExportModal: React.FC<ReferralExportModalProps> = ({
  opened,
  onClose,
}) => {
  const [selectedColumns, setSelectedColumns] =
    useState<string[]>(DEFAULT_COLUMNS);
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [internalExpanded, setInternalExpanded] = useState(false);

  // Get all column values across all categories
  const allColumns = Object.values(COLUMN_CATEGORIES).flatMap((cat) =>
    cat.columns.map((col) => col.value)
  );

  // Get all user-facing columns (exclude internal)
  const userFacingColumns = allColumns.filter(
    (col) => !COLUMN_CATEGORIES.internal.columns.some((c) => c.value === col)
  );

  const handleSelectAll = () => {
    setSelectedColumns(allColumns);
  };

  const handleDeselectAll = () => {
    setSelectedColumns([]);
  };

  const handleSelectUserFacing = () => {
    setSelectedColumns(userFacingColumns);
  };

  const handleColumnToggle = (column: string) => {
    setSelectedColumns((prev) =>
      prev.includes(column)
        ? prev.filter((c) => c !== column)
        : [...prev, column]
    );
  };

  const handleCategoryToggle = (categoryKey: string) => {
    const category =
      COLUMN_CATEGORIES[categoryKey as keyof typeof COLUMN_CATEGORIES];
    const categoryColumns = category.columns.map((col) => col.value);
    const allSelected = categoryColumns.every((col) =>
      selectedColumns.includes(col)
    );

    if (allSelected) {
      // Deselect all columns in this category
      setSelectedColumns((prev) =>
        prev.filter((col) => !categoryColumns.includes(col))
      );
    } else {
      // Select all columns in this category
      setSelectedColumns((prev) => [
        ...prev,
        ...categoryColumns.filter((col) => !prev.includes(col)),
      ]);
    }
  };

  const isCategorySelected = (categoryKey: string) => {
    const category =
      COLUMN_CATEGORIES[categoryKey as keyof typeof COLUMN_CATEGORIES];
    const categoryColumns = category.columns.map((col) => col.value);
    return categoryColumns.every((col) => selectedColumns.includes(col));
  };

  const isCategoryIndeterminate = (categoryKey: string) => {
    const category =
      COLUMN_CATEGORIES[categoryKey as keyof typeof COLUMN_CATEGORIES];
    const categoryColumns = category.columns.map((col) => col.value);
    const selectedInCategory = categoryColumns.filter((col) =>
      selectedColumns.includes(col)
    );
    return (
      selectedInCategory.length > 0 &&
      selectedInCategory.length < categoryColumns.length
    );
  };

  const handleExport = async () => {
    if (selectedColumns.length === 0) {
      notifications.show({
        title: "No Columns Selected",
        message: "Please select at least one column to export",
        color: "yellow",
      });
      return;
    }

    try {
      setIsExporting(true);
      const blob = await exportReferralCSV({
        columns: selectedColumns,
        dateFrom,
        dateTo,
      });

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `referrals_export_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      notifications.show({
        title: "Success",
        message: `Exported ${selectedColumns.length} columns successfully`,
        color: "green",
      });

      // Close modal after successful export
      onClose();
    } catch (error) {
      console.error("Export failed:", error);
      notifications.show({
        title: "Export Failed",
        message: "Failed to export referral data. Please try again.",
        color: "red",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Export Referral Data"
      size="lg"
      centered
    >
      <Stack gap="md">
        {/* Quick selection buttons */}
        <Group gap="xs">
          <Button size="xs" variant="light" onClick={handleSelectUserFacing}>
            Select Default
          </Button>
          <Button size="xs" variant="light" onClick={handleSelectAll}>
            Select All
          </Button>
          <Button size="xs" variant="light" onClick={handleDeselectAll}>
            Deselect All
          </Button>
        </Group>

        <Text size="sm" c="dimmed">
          Selected: {selectedColumns.length} column
          {selectedColumns.length !== 1 ? "s" : ""}
        </Text>

        <Divider />

        {/* Column selection */}
        <Stack gap="lg">
          {Object.entries(COLUMN_CATEGORIES).map(([categoryKey, category]) => {
            // Handle internal category separately (collapsible)
            if (categoryKey === "internal") {
              return (
                <Box key={categoryKey}>
                  <Group
                    gap="xs"
                    onClick={() => setInternalExpanded(!internalExpanded)}
                    style={{ cursor: "pointer" }}
                  >
                    {internalExpanded ? (
                      <IconChevronUp size={16} />
                    ) : (
                      <IconChevronDown size={16} />
                    )}
                    <Checkbox
                      label={category.label}
                      checked={isCategorySelected(categoryKey)}
                      indeterminate={isCategoryIndeterminate(categoryKey)}
                      onChange={() => handleCategoryToggle(categoryKey)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Text size="xs" c="dimmed">
                      (Advanced)
                    </Text>
                  </Group>
                  <Collapse in={internalExpanded}>
                    <Stack gap="xs" ml="xl" mt="xs">
                      {category.columns.map((col) => (
                        <Checkbox
                          key={col.value}
                          label={col.label}
                          checked={selectedColumns.includes(col.value)}
                          onChange={() => handleColumnToggle(col.value)}
                        />
                      ))}
                    </Stack>
                  </Collapse>
                </Box>
              );
            }

            // Regular categories (always visible)
            return (
              <Box key={categoryKey}>
                <Checkbox
                  label={category.label}
                  checked={isCategorySelected(categoryKey)}
                  indeterminate={isCategoryIndeterminate(categoryKey)}
                  onChange={() => handleCategoryToggle(categoryKey)}
                  mb="xs"
                />
                <Stack gap="xs" ml="xl">
                  {category.columns.map((col) => (
                    <Checkbox
                      key={col.value}
                      label={col.label}
                      checked={selectedColumns.includes(col.value)}
                      onChange={() => handleColumnToggle(col.value)}
                    />
                  ))}
                </Stack>
              </Box>
            );
          })}
        </Stack>

        <Divider />

        {/* Date range filter */}
        <Stack gap="sm">
          <Text size="sm" fw={500}>
            Date Range Filter (Optional)
          </Text>
          <Group grow>
            <DatePickerInput
              label="From Date"
              placeholder="Select start date"
              value={dateFrom}
              onChange={(value) => setDateFrom(value as unknown as Date | null)}
              clearable
              maxDate={dateTo || undefined}
            />
            <DatePickerInput
              label="To Date"
              placeholder="Select end date"
              value={dateTo}
              onChange={(value) => setDateTo(value as unknown as Date | null)}
              clearable
              minDate={dateFrom || undefined}
            />
          </Group>
        </Stack>

        <Divider />

        {/* Action buttons */}
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose} disabled={isExporting}>
            Cancel
          </Button>
          <Button
            leftSection={<IconDownload size={16} />}
            onClick={handleExport}
            loading={isExporting}
            disabled={selectedColumns.length === 0}
          >
            Export CSV
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ReferralExportModal;
